import { useRef } from 'react';

// Touch-only swipe detection. Listens to TOUCH events exclusively, so mouse /
// trackpad input on touchscreen laptops is never intercepted (clicks, drags,
// and the waveform's mouse handlers all keep working).
//
// opts:
//   onLeft / onRight  — horizontal swipes (left = finger moves right→left)
//   onUp   / onDown   — vertical swipes
//   threshold (px)    — min travel on the dominant axis to count as a swipe (def 45)
//   maxOff            — max travel on the OTHER axis, as a ratio of the dominant
//                       (def 0.8) so a diagonal scroll doesn't fire a swipe
//   maxMs             — max duration; slower = a drag/scroll, not a swipe (def 700)
//   ignore            — CSS selector; a touch STARTING inside it is skipped
//                       (e.g. '.wave' so a waveform drag isn't read as nav)
//
// Returns { onTouchStart, onTouchEnd } to spread onto a container. We never call
// preventDefault, so native vertical scrolling is preserved.
export function useSwipe({ onLeft, onRight, onUp, onDown, threshold = 45, maxOff = 0.8, maxMs = 700, ignore } = {}) {
  const start = useRef(null);

  function onTouchStart(e) {
    if (e.touches.length !== 1) { start.current = null; return; }   // multi-touch = pinch/zoom, ignore
    if (ignore && e.target.closest && e.target.closest(ignore)) { start.current = null; return; }
    const t = e.touches[0];
    start.current = { x: t.clientX, y: t.clientY, t: Date.now() };
  }

  function onTouchEnd(e) {
    const s = start.current; start.current = null;
    if (!s || !e.changedTouches.length) return;
    if (Date.now() - s.t > maxMs) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    const adx = Math.abs(dx), ady = Math.abs(dy);
    if (adx >= ady) {                                  // horizontal dominant
      if (adx < threshold || ady > adx * maxOff) return;
      (dx < 0 ? onLeft : onRight)?.();
    } else {                                           // vertical dominant
      if (ady < threshold || adx > ady * maxOff) return;
      (dy < 0 ? onUp : onDown)?.();
    }
  }

  return { onTouchStart, onTouchEnd };
}
